import {SelectionModel} from "@angular/cdk/collections";
import {AfterViewInit, Component, Injector, OnInit} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {DateRange} from "@angular/material/datepicker";
import {BasePageComponent} from "@core-components/page-content/base-page.component";
import {TabManagementService} from "@core-services/tab-management.service";
import {PageEventInterface} from "@core/interfaces/page-event.interface";
import {CrudLease} from "@features/leasing/crud-lease.model";
import {LeaseBookingsComponent} from "@features/leasing/lease-bookings/lease-bookings.component";
import {DateUtilsService} from "@shared-services/dateUtils.service";
import {UtilsService} from "@shared-services/utils.service";
import {LoadingService} from "@ui-kit/framework/util/service/loading.service";
import {
  CustomDateRangeChangeEventModel
} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range-change-event.model";
import {CustomDateRangeModel, TimeFrames} from "@ui-kit/webkit/components/custom-date-filter/custom-date-range.model";
import {Retailer} from "../../../leads/retailers/retailers.model";
import {LeaseViewComponent} from "../../lease-view/lease-view.component";
import {AgreementType, LeaseFilterType, LeaseStatus, LeaseSummary, OccupancyType} from "../../leasing.model";
import {LeasingService} from "../../leasing.service";

export enum DefaultTableViewMode {
  COMPACT,
  FULL
}

const defaultPageHeader = {
  title: 'Plan your next lease',
  description: 'View and manage your planned leases, such as the dates and terms of your next lease. You can also see any invalid planned leases so that you never miss an opportunity to fill your spaces.'
};

const defaultAllColumns: string[] = ['uuid', 'retailerCompanyName', 'leaseStartDate', 'leaseEndDate', 'spaceNames', 'spaceSize', 'rentalType', 'monthlyFee', 'occupancyType', 'leaseStatus', 'expiresIn', 'tradingOutsideOfLeasePeriod'];
const defaultCompactColumns: string[] = ['retailerCompanyName', 'leaseStartDate', 'leaseEndDate', 'spaceNames', 'spaceSize', 'occupancyType', 'leaseStatus', 'tradingOutsideOfLeasePeriod'];

@Component({
  selector: 'app-lease-schedule-list',
  templateUrl: './lease-schedule-list.component.html',
  styleUrls: ['./lease-schedule-list.component.scss']
})
export class LeaseScheduleListComponent extends BasePageComponent implements OnInit, AfterViewInit {
  protected readonly OccupancyType = OccupancyType;

  pageHeader!: any;
  allColumns: string[];
  compactColumns: string[];
  displayedColumns: string[];
  tableViewMode: DefaultTableViewMode = DefaultTableViewMode.FULL;

  currentPage = 0;
  pageSize = 1000;
  endpointSuffix = 'lease-schedule';

  filterTypes = [LeaseFilterType.LEASE_STATUS, LeaseFilterType.OCCUPANCY_TYPE, LeaseFilterType.TERM_STATUS, LeaseFilterType.EXPIRES_IN, LeaseFilterType.DATE_RANGE, LeaseFilterType.COMPACT_VIEW];

  baseLeaseData: CrudLease[] = [];
  filteredLeaseData: CrudLease[] = [];
  selection = new SelectionModel<Retailer>(true, []);
  leaseStatuses = [LeaseStatus.PLANNED, LeaseStatus.APPROVED, LeaseStatus.ACTIVE, LeaseStatus.EXPIRED, LeaseStatus.INVALID];
  occupancyTypes = [OccupancyType.SINGLE, OccupancyType.SHARED];
  filterForm: FormGroup;
  dateRange: DateRange<Date | null> = new DateRange<Date | null>(null, null);

  dateFilters: CustomDateRangeModel[] = [
    new CustomDateRangeModel('1', 2, TimeFrames.DAYS),
    new CustomDateRangeModel('2', 1, TimeFrames.WEEK),
    new CustomDateRangeModel('3', 1, TimeFrames.MONTH),
  ]

  constructor(injector: Injector,
              public utils: UtilsService,
              public dateUtils: DateUtilsService,
              private tabService: TabManagementService,
              private leasingService: LeasingService,
              public loader: LoadingService) {
    super(injector);

    this.pageHeader = this.data.pageHeader ?? defaultPageHeader;

    if (this.data.lease.leaseStatuses && this.data.lease.leaseStatuses.length > 0) {
      this.leaseStatuses = this.data.lease.leaseStatuses;
    }

    if (this.data.lease.filterTypes && this.data.lease.filterTypes.length > 0) {
      this.filterTypes = this.data.lease.filterTypes;
    }

    if (this.data.lease.endpointSuffix) {
      this.endpointSuffix = this.data.lease.endpointSuffix;
    }

    this.allColumns = this.excludeColumns(this.data.table?.allColumns ?? defaultAllColumns, this.data.table?.excludeDisplayColumns ?? []);
    this.compactColumns = this.excludeColumns(this.data.table?.compactColumns ?? defaultCompactColumns, this.data.table?.excludeDisplayColumns ?? []);
    this.displayedColumns = this.compactColumns;

    if (this.data.table?.tableViewMode) {
      this.tableViewMode = this.data.table.tableViewMode;
      const viewModeMap = {
        [DefaultTableViewMode.COMPACT]: this.compactColumns,
        [DefaultTableViewMode.FULL]: this.allColumns
      };
      this.displayedColumns = viewModeMap[this.data.table.tableViewMode as DefaultTableViewMode] ?? this.compactColumns;
    }

    this.filterForm = new FormGroup({
      leaseStatus: new FormControl([]),
      occupancyType: new FormControl([]),
      tradingOutsideOfLeasePeriod: new FormControl([]),
      expiresIn: new FormControl([]),
    })
  }


  ngOnInit() {
    this.loadInitialLeaseData();
  }

  ngAfterViewInit() {
    this.filterForm.valueChanges.subscribe(() => {
      this.filterLeaseData();
    });
  }

  loadInitialLeaseData() {
    this.currentPage = 0;
    this.baseLeaseData = [];
    this.loadLeases();
  }

  loadMoreLeases() {
    this.currentPage++;
    this.loadLeases();
  }

  loadLeases() {
    const allData = [...this.baseLeaseData];
    const baseFilters: { name: string, val: any }[] = [
      {name: "agreementType", val: AgreementType.LEASE},
      {name: "statusList", val: this.leaseStatuses},
      {name: "typeList", val: this.occupancyTypes},
    ];
    if (this.dateRange.start && this.dateRange.end) {
      baseFilters.push(...[
        {name: "leaseStartDate", val: this.dateUtils.displayShortDate(this.dateRange.start)},
        {name: "leaseEndDate", val: this.dateUtils.displayShortDate(this.dateRange.end)}
      ]);
    }

    this.leasingService.getAll(`/${this.endpointSuffix}`, {
      page: this.currentPage.toString(),
      size: this.pageSize.toString(),
      sort: 'retailerCompanyName,asc'
    }, baseFilters)
      .subscribe({
        next: (value: { content: LeaseSummary[] }) => {
          const newData = value.content.map(m => new CrudLease(m));
          allData.push(...newData);
          this.baseLeaseData = [...allData];
          this.filterLeaseData();
        },
        error: (err) => {
          console.log(err);
        }
      })
  }

  filterLeaseData() {
    this.filteredLeaseData = this.baseLeaseData.filter(lease => {

      const statusFilter = this.filterForm.get('leaseStatus')?.value;
      const occupancyTypeFilter = this.filterForm.get('occupancyType')?.value;
      const tradingStatusFilter = this.filterForm.get('tradingOutsideOfLeasePeriod')?.value;
      const expiresInFilter = this.filterForm.get('expiresIn')?.value;

      const statusIncluded = this.leaseStatuses && this.leaseStatuses.includes(lease.leaseStatus);
      const statusValid: boolean = statusFilter && statusFilter.length == 0 || statusFilter.includes(lease.leaseStatus);
      const occupancyValid: boolean = occupancyTypeFilter && occupancyTypeFilter.length == 0 || occupancyTypeFilter.includes(lease.occupancyType);
      const tradingStatusValid: boolean = tradingStatusFilter && tradingStatusFilter.length == 0 || tradingStatusFilter.includes(lease.tradingOutsideOfLeasePeriod);
      const expiresInValid: boolean = expiresInFilter && expiresInFilter.length == 0 || (lease.expiresInDays != null && Math.max(...expiresInFilter) >= lease.expiresInDays);

      return statusIncluded && statusValid && occupancyValid && tradingStatusValid && expiresInValid;
    })
  }

  openLeaseViewTab(lease: LeaseSummary) {
    let payload: PageEventInterface = {
      componentToRegister: LeaseViewComponent,
      pageName: 'Lease ' + this.utils.displayUuid(lease.uuid),
      pageHeader: this.offering!.label,
      data: {
        id: lease.uuid,
        occupancyType: lease.occupancyType,
        agreementType: lease.agreementType
      },
      id: this.utils.generateUuidWithPrefix('lease'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leasing',
      pageCode: this.data.lease.pageCode,
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  getDateRange(event: CustomDateRangeChangeEventModel) {
    this.dateRange = event.dateRange;
    this.loadInitialLeaseData();
  }

  openCreateLeaseTab() {
    let payload: PageEventInterface = {
      componentToRegister: LeaseBookingsComponent,
      pageName: 'Create a lease',
      pageHeader: this.offering!.label,
      data: {},
      id: this.utils.generateUuidWithPrefix('lease-bookings'),
      offeringId: this.offering!.offeringUuid,
      sectionCode: 'leasing',
      pageCode: 'lease-bookings',
      offering: this.offering
    }
    this.tabService.sendPageAddEvent(payload);
  }

  onTableViewModeChange() {
    const columnsToShow = this.tableViewMode === DefaultTableViewMode.COMPACT ? this.compactColumns : this.allColumns;
    this.displayedColumns = this.excludeColumns(columnsToShow, this.data.table?.excludeDisplayColumns ?? []);
  }

  private excludeColumns(columns: string[], valuesToExclude: string[]): string[] {
    return columns.filter(col => !valuesToExclude.includes(col));
  }

  protected readonly CrudLease = CrudLease;
  protected readonly DefaultTableViewMode = DefaultTableViewMode;
  protected readonly LeaseFilterType = LeaseFilterType;
}
